import * as React from 'react';

interface UseRowSelectionParams {
  /** Only `.length` and indices are used; passed for the reset-effect dependency. */
  rows: readonly unknown[];
  /** Stable identity per row. With it, selection survives re-sorts/refetches and
   *  can be persisted by a parent; without it, selection falls back to row index. */
  getRowKey?: (rowIndex: number) => string;
  /** Controlled selection: pass both to let a parent own the selected-row keys. */
  selectedKeys?: Set<string>;
  onSelectedKeysChange?: (next: Set<string>) => void;
}

export interface RowSelection {
  /** Indices of the currently-rendered rows whose key is selected. */
  selectedIndices: number[];
  count: number;
  allSelected: boolean;
  isSelected: (rowIndex: number) => boolean;
  toggleRow: (rowIndex: number) => void;
  toggleAll: () => void;
  clear: () => void;
}

/**
 * Row-selection state for a table, keyed by a stable row key. Mirrors the
 * `useColumnManager` split: this hook owns the logic, `SelectionActionBar`
 * owns the bulk-bar UI. Works controlled (parent owns the keys) or
 * uncontrolled (internal state).
 */
export function useRowSelection({
  rows,
  getRowKey,
  selectedKeys,
  onSelectedKeysChange,
}: UseRowSelectionParams): RowSelection {
  const rowKey = (index: number) => getRowKey?.(index) ?? String(index);

  const [internal, setInternal] = React.useState<Set<string>>(new Set());
  const isControlled = selectedKeys !== undefined && onSelectedKeysChange !== undefined;
  const selected = isControlled ? selectedKeys! : internal;
  const setSelected = (next: Set<string>) => {
    if (isControlled) onSelectedKeysChange!(next);
    else setInternal(next);
  };

  React.useEffect(() => {
    // Index-based selection (no stable key) goes stale on any data change, so
    // clear it then.
    if (!getRowKey) {
      setSelected(new Set<string>());
      return;
    }
    // With a stable key, sorting/refetching keeps the selection valid — but
    // prune keys for rows that are gone (e.g. after a bulk delete) so the count
    // reflects reality. Skip while there are no rows (initial load / cache miss)
    // to avoid wiping a persisted selection.
    if (rows.length === 0) return;
    const present = new Set(rows.map((_, i) => rowKey(i)));
    const pruned = new Set([...selected].filter((k) => present.has(k)));
    if (pruned.size !== selected.size) setSelected(pruned);
  }, [rows, getRowKey]);

  // Keys for rows not present (e.g. filtered out) are simply ignored here.
  const selectedIndices = rows.map((_, i) => i).filter((i) => selected.has(rowKey(i)));
  const allSelected = rows.length > 0 && rows.every((_, i) => selected.has(rowKey(i)));

  const toggleRow = (index: number) => {
    const key = rowKey(index);
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
  };

  const toggleAll = () => {
    setSelected(allSelected ? new Set<string>() : new Set(rows.map((_, i) => rowKey(i))));
  };

  return {
    selectedIndices,
    count: selectedIndices.length,
    allSelected,
    isSelected: (index) => selected.has(rowKey(index)),
    toggleRow,
    toggleAll,
    clear: () => setSelected(new Set<string>()),
  };
}
